import { SUPPORTED_EMAIL_LANGS } from "components/Beneficiar/utils";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { t } from "i18next";
import { useMemo } from "react";
import { AgreementSignatureStatus } from "store/agreementsStore";
import type { ManagementArticle } from "store/articleStore";
import type { ICompany } from "store/settingsStore";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

import type { TermData } from ".";

const resourceKey = (portalType: TPortalType, lng: string) => {
    if (portalType === EPortalType.PRESS) return t("SendEmail.Article", { lng });
    if (portalType === EPortalType.AUDIOVISUAL) return t("SendEmail.Service", { lng });
    return t("SendEmail.Title", { lng });
};

/**
 * Build per-language default subject/body maps for bulk press sends.
 * Backend picks the entry matching each beneficiary's `lang`.
 * `{{title}}`, `{{name}}`, `{{parution}}` stay unresolved for per-benef substitution.
 */
export const buildBulkSubjectByLang = ({ defaultSubject }: { defaultSubject?: string }) => {
    const map: Record<string, string> = {};
    for (const lng of SUPPORTED_EMAIL_LANGS) {
        map[lng] = defaultSubject ? defaultSubject : `${t("SendEmail.StatementFor", { lng })} "{{title}}"`;
    }
    return map;
};

export const buildBulkBodyByLang = ({
    defaultBody,
    portalType,
    companyName,
}: {
    defaultBody?: string;
    portalType: TPortalType;
    companyName: string;
}) => {
    const map: Record<string, string> = {};
    for (const lng of SUPPORTED_EMAIL_LANGS) {
        map[lng] = defaultBody
            ? defaultBody
            : t("SendEmail.DefaultEmailBulk", {
                  resource: resourceKey(portalType, lng),
                  companyName: companyName ? `,\n${companyName}` : "",
                  lng,
              });
    }
    return map;
};

interface IEmailContent {
    defaultBody: string | undefined;
    isBulk: boolean;
    termData: TermData;
    beneficiar: IBeneficiar;
    portalType: TPortalType;
    companySettings: ICompany | undefined;
    lang: string;
}

export const getEmailContent = ({
    defaultBody,
    isBulk,
    termData,
    beneficiar,
    portalType,
    companySettings,
    lang,
}: IEmailContent) => {
    /**
     * If body is empty string, it means they haven't changed the default text set by Crealo.
     */
    const isBodyDefault = defaultBody === "";
    const usePublicationVariable = !!termData?.issue;

    if (isBodyDefault) {
        if (isBulk) {
            return t("SendEmail.DefaultEmailBulk", {
                resource: resourceKey(portalType, lang),
                companyName: companySettings?.name ? `,\n${companySettings?.name}` : "",
                lng: lang,
            });
        } else {
            return t("SendEmail.DefaultEmailSimple", {
                name: beneficiar?.name ?? "",
                surname: beneficiar?.surname ?? "",
                resource: resourceKey(portalType, lang),
                title: termData.title ?? "",
                companyName: companySettings?.name ? `,\n${companySettings?.name}` : "",
                lng: lang,
            });
        }
    } else {
        if (isBulk) {
            return defaultBody;
        } else {
            if (usePublicationVariable) {
                return defaultBody
                    ?.replace("{{name}}", `${beneficiar?.name ?? ""} ${beneficiar?.surname ?? ""}`)
                    .replace("{{title}}", termData?.title)
                    .replace("{{parution}}", termData?.issue ?? "");
            } else {
                return defaultBody
                    ?.replace("{{name}}", `${beneficiar?.name ?? ""} ${beneficiar?.surname ?? ""}`)
                    .replace("{{title}}", termData?.title);
            }
        }
    }
};

interface ISubjectContent {
    defaultSubject: string | undefined;
    isBulk: boolean;
    termData: TermData;
    beneficiar: IBeneficiar;
    lang: string;
}
export const getEmailSubject = ({ defaultSubject, isBulk, termData, beneficiar, lang }: ISubjectContent) => {
    const isSubjectDefault = defaultSubject === "";

    if (isSubjectDefault) {
        if (isBulk) {
            return `${t("SendEmail.StatementFor", { lng: lang })} "{{title}}"`;
        } else {
            return `${t("SendEmail.StatementFor", { lng: lang })} "${termData.title ?? ""}"`;
        }
    } else {
        if (isBulk) {
            return defaultSubject;
        } else {
            return defaultSubject
                ?.replace("{{title}}", termData?.title ?? "")
                .replace("{{name}}", `${beneficiar?.name ?? ""} ${beneficiar?.surname ?? ""}`);
        }
    }
};

/**
 * Transform articles data for prop termData of SendEmailComponent (ex: used for press management page)
 * Transform articles data for prop termData of Send Agreements to sign (ex: used for press management page)
 * Can be transformed for publisher of type "book" later
 * @param articles
 */
export const useSelectedArticlesDataTransform = (
    articles: ManagementArticle[] | undefined,
    selectedArticleIds: number[],
) => {
    return useMemo(() => {
        if (!articles) return;
        const articleForEmail =
            selectedArticleIds.length === 1
                ? articles.find((article) => article.id === selectedArticleIds[0])
                : ({} as ManagementArticle);
        if (!articleForEmail) return;

        // Transform data for send email
        const sendEmailTermData = {
            beneficiaryId: articleForEmail.beneficiaryId,
            author: articleForEmail.beneficiary,
            title: articleForEmail.name,
            email: articleForEmail.email,
            precount: `${articleForEmail.precount}`,
            issue: articleForEmail.titleName,
            contractSigned: articleForEmail.contractSigned,
        } as TermData;

        // Transform data for send agreement for signature
        const articlesForAgreementsData = articles.filter(
            (art) => selectedArticleIds.includes(art.id) && art.contractSigned === AgreementSignatureStatus.TO_SIGN,
        );

        return { sendEmailTermData, articlesForAgreementsData };
    }, [selectedArticleIds, articles]);
};
